import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AudienceSelectionRef,
  QuestionnaireReviewOptions,
} from "@digital-twins/shared-types";
import { persistPlannedSampleSize } from "../utils/plannedSampleSize";
import {
  persistReviewOptions,
  type ReviewDraftWrite,
} from "../utils/reviewDraftWrite";
import type { ReviewPin } from "../state/creationState";
import type { LaunchOutcome } from "../launchOutcome";

const SAMPLE_SIZE_SAVE_DEBOUNCE_MS = 500;

interface ReviewDraftWritesInput {
  currentStep: number;
  questionnaireDraftId: string | null;
  numRespondents: number | null;
  reviewedRevision: number | null;
  reviewedAudience: AudienceSelectionRef | null;
  /** A Review write moved the pinned revision `from` to `revision`. */
  onRepin: (repin: {
    from: number;
    revision: number;
    audience: AudienceSelectionRef;
  }) => void;
}

interface SampleSizeSaveBook {
  draftId: string | null;
  generation: number;
  lastPersistedSampleSize: number | null;
  saveTarget: number | null;
}


interface InFlightReviewWrite {
  field: "sampleSize" | "reviewOptions";
  draftId: string;
  generation: number;
  task: Promise<void>;
}

function savesForDraftGeneration(
  saves: Array<InFlightReviewWrite>,
  draftId: string | null,
  generation: number,
): Array<InFlightReviewWrite> {
  if (draftId == null) {
    return [];
  }
  return saves.filter(
    (save) => save.draftId === draftId && save.generation === generation,
  );
}

/**
 * Review's writes to the draft: the sample size, and the launch options
 * (`writeReviewOptions`, called when the user changes one). Neither lands
 * while a launch is reading the same draft: launch waits for both.
 *
 * Each write is a compare-and-swap on Review's pin. A write that lands moves
 * the draft one revision on, changing only what it wrote, so the pin moves
 * with it: `pinRef` at once, inside the write (a write queued behind it, and
 * a launch waiting on it, read the new revision before React renders), and
 * the flow's state through `onRepin`.
 *
 * Bookkeeping is scoped to the draft id and a generation that bumps when
 * the draft changes. A save started for the previous draft cannot mark the
 * next draft as already saved.
 *
 * A new size is written immediately while a save is already in flight,
 * including a return to the size on screen before that baseline save has
 * been recorded. A debounced follow-up is cancelled when review closes,
 * and the in-flight size would otherwise be what a reload shows.
 *
 * In-flight saves are tracked per draft and generation. Launch waits only
 * for the draft it is about to read. A stalled PATCH for another draft
 * cannot race that read, so it must not hold launch open.
 */
export function useReviewDraftWrites(
  input: ReviewDraftWritesInput,
): {
  writeReviewOptions: (reviewOptions: QuestionnaireReviewOptions) => void;
  launchAfterReviewWritesSettle: (
    launch: (pin: ReviewPin) => Promise<LaunchOutcome>,
  ) => Promise<LaunchOutcome>;
} {
  const {
    currentStep,
    questionnaireDraftId,
    numRespondents,
    reviewedRevision,
    reviewedAudience,
  } = input;
  const pinRef = useRef<ReviewPin>({
    revision: reviewedRevision,
    audience: reviewedAudience,
  });
  pinRef.current = { revision: reviewedRevision, audience: reviewedAudience };
  const onRepinRef = useRef(input.onRepin);
  onRepinRef.current = input.onRepin;
  const [suspended, setSuspended] = useState(false);
  const suspendedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef<Array<InFlightReviewWrite>>([]);
  const launchingRef = useRef(false);
  const mountedRef = useRef(true);
  const stepRef = useRef(currentStep);
  const bookRef = useRef<SampleSizeSaveBook>({
    draftId: null,
    generation: 0,
    lastPersistedSampleSize: null,
    saveTarget: null,
  });
  stepRef.current = currentStep;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Track one Review write until it settles: re-pin Review when it landed on
   * the same draft, and hold launch until then.
   */
  const trackWrite = useCallback(
    (
      field: InFlightReviewWrite["field"],
      draftId: string,
      generation: number,
      write: Promise<ReviewDraftWrite>,
      onSettled: (write: ReviewDraftWrite, sameDraft: boolean) => void,
    ) => {
      const task = write
        .then((written) => {
          const current = bookRef.current;
          const sameDraft =
            current.generation === generation && current.draftId === draftId;
          if (written.kind === "saved" && sameDraft) {
            onRepinRef.current(written);
          }
          onSettled(written, sameDraft);
        })
        .catch((error: unknown) => {
          console.error(`Failed to save Review's ${field}:`, error);
        });
      const entry: InFlightReviewWrite = { field, draftId, generation, task };
      inFlightRef.current.push(entry);
      void task.finally(() => {
        inFlightRef.current = inFlightRef.current.filter((save) => save !== entry);
      });
    },
    [],
  );

  const startSave = useCallback(
    (draftId: string, sampleSize: number, generation: number) => {
      bookRef.current.saveTarget = sampleSize;
      trackWrite(
        "sampleSize",
        draftId,
        generation,
        persistPlannedSampleSize(draftId, sampleSize, pinRef),
        (write, sameDraft) => {
          const current = bookRef.current;
          if (
            (write.kind === "saved" || write.kind === "unchanged") &&
            sameDraft &&
            current.saveTarget === sampleSize
          ) {
            current.lastPersistedSampleSize = sampleSize;
          }
        },
      );
    },
    [trackWrite],
  );

  const writeReviewOptions = useCallback(
    (reviewOptions: QuestionnaireReviewOptions) => {
      const { draftId, generation } = bookRef.current;
      if (!draftId) {
        return;
      }
      trackWrite(
        "reviewOptions",
        draftId,
        generation,
        persistReviewOptions(draftId, reviewOptions, pinRef),
        () => undefined,
      );
    },
    [trackWrite],
  );

  useEffect(() => {
    const draftId = questionnaireDraftId;
    if (bookRef.current.draftId !== draftId) {
      bookRef.current = {
        draftId,
        generation: bookRef.current.generation + 1,
        lastPersistedSampleSize: null,
        saveTarget: null,
      };
    }

    const sampleSize = numRespondents;
    const book = bookRef.current;
    if (suspended || currentStep < 3 || !draftId || sampleSize == null) {
      return;
    }

    // An in-flight save already passed the debounce. The next target has to
    // join that queue now: a timer started here is cleared if review closes
    // before it fires, and the in-flight size is what stays stored.
    const saveInFlight = savesForDraftGeneration(
      inFlightRef.current,
      draftId,
      book.generation,
    ).some((save) => save.field === "sampleSize");
    if (
      saveInFlight ||
      (book.lastPersistedSampleSize === sampleSize &&
        book.saveTarget !== sampleSize)
    ) {
      if (book.saveTarget !== sampleSize) {
        startSave(draftId, sampleSize, book.generation);
      }
      return;
    }

    const generation = book.generation;
    book.saveTarget = sampleSize;
    const timeoutId = window.setTimeout(() => {
      if (suspendedRef.current || bookRef.current.generation !== generation) {
        return;
      }
      if (bookRef.current.saveTarget !== sampleSize) {
        return;
      }
      startSave(draftId, sampleSize, generation);
    }, SAMPLE_SIZE_SAVE_DEBOUNCE_MS);
    timerRef.current = timeoutId;

    return () => {
      window.clearTimeout(timeoutId);
      if (timerRef.current === timeoutId) {
        timerRef.current = null;
      }
    };
  }, [currentStep, questionnaireDraftId, numRespondents, startSave, suspended]);

  const launchAfterReviewWritesSettle = useCallback(
    async (
      launch: (pin: ReviewPin) => Promise<LaunchOutcome>,
    ): Promise<LaunchOutcome> => {
      // The one busy check: a launch is already under way.
      if (launchingRef.current) {
        return { kind: "aborted", reason: "busy" };
      }
      launchingRef.current = true;
      // Generation moves on every draft id change, including away and back.
      // The id alone matches again after A→B→A, so it cannot see that trip.
      const draftIdAtLaunch = bookRef.current.draftId;
      const generationAtLaunch = bookRef.current.generation;
      const stepAtLaunch = stepRef.current;
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      suspendedRef.current = true;
      setSuspended(true);
      const pending = savesForDraftGeneration(
        inFlightRef.current,
        draftIdAtLaunch,
        generationAtLaunch,
      ).map((save) => save.task);
      try {
        await Promise.all(pending);
        const leftTheDraft =
          !mountedRef.current ||
          bookRef.current.generation !== generationAtLaunch ||
          stepRef.current !== stepAtLaunch;
        if (leftTheDraft) {
          return { kind: "aborted", reason: "left_the_draft" };
        }
        // The pin as the settled writes left it, not the click's render.
        return await launch(pinRef.current);
      } finally {
        launchingRef.current = false;
        suspendedRef.current = false;
        setSuspended(false);
      }
    },
    [],
  );

  return { writeReviewOptions, launchAfterReviewWritesSettle };
}
